import { request } from './client';

interface ListResponse<T> {
  items: T[];
}

export interface Capability {
  id: string;
  name: string;
  slug: string;
  description?: string | null;
}

export interface ConnectionSource {
  id: string;
  slug: string;
  name: string;
  description?: string | null;
  capabilities: Array<Capability & {
    sync_method?: string | null;
  }>;
}

export interface ConnectionRecord {
  id: string;
  name: string;
  status?: string | null;
  last_sync_at?: string | null;
  created_at: string;
  updated_at: string;
}

export interface ConnectionWithSource {
  connection: ConnectionRecord;
  source: ConnectionSource;
}

/** Server wire shape: connection fields are flattened next to `source`. */
interface ConnectionWire extends ConnectionRecord {
  source: ConnectionSource;
}

function normalizeConnection({ source, ...connection }: ConnectionWire): ConnectionWithSource {
  return { connection, source };
}

export function listConnections(): Promise<ConnectionWithSource[]> {
  return request<ListResponse<ConnectionWire>>('/connections')
    .then((response) => response.items.map(normalizeConnection));
}

export function renameConnection(id: string, name: string): Promise<ConnectionWithSource> {
  return request<ConnectionWire>(`/connections/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: { name },
    noRetry: true,
  }).then(normalizeConnection);
}

/**
 * Removes credentials and stops syncing while preserving the connection's
 * point-in-time data. This is deliberately separate from destroyConnection.
 */
export function disconnectConnection(id: string): Promise<void> {
  return request<void>(`/connections/${encodeURIComponent(id)}/disconnect`, {
    method: 'POST',
    body: {},
    noRetry: true,
  });
}
